import React, { useMemo, useState } from 'react';
import { Chip, ProviderBadge } from './Chip.jsx';
import { IconSearch, IconX, IconChevDown, IconTrash, IconPin, IconSignal } from './Icons.jsx';
import { providerOf } from './providers.js';
import { formatLocalDateTime, shortIdOf } from './util.js';

export function FilterBar({ filters, setFilters, events, count, pinnedCount = 0, onClearAll }) {
  const statusCounts = useMemo(() => {
    const m = { '2xx': 0, '4xx': 0, '5xx': 0 };
    events.forEach(e => {
      const c = e.responseCode;
      if (c >= 200 && c < 300) m['2xx']++;
      else if (c >= 400 && c < 500) m['4xx']++;
      else if (c >= 500) m['5xx']++;
    });
    return m;
  }, [events]);

  return (
    <div style={{
      margin: '16px 24px 0', padding: 12,
      background: '#fff', border: '1px solid var(--border-default)', borderRadius: 12,
      display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '7px 12px', background: 'var(--neutral-25)',
        border: '1px solid var(--border-default)', borderRadius: 8,
        flex: '1 1 260px', minWidth: 220,
      }}>
        <IconSearch size={14} stroke="var(--fg-3)" />
        <input
          placeholder="Search any value — payload, headers, IP, event ID…"
          value={filters.q}
          onChange={e => setFilters(f => ({ ...f, q: e.target.value }))}
          style={{
            flex: 1, border: 0, outline: 'none', background: 'transparent',
            fontSize: 13, color: 'var(--fg-1)',
          }}
        />
        {filters.q && (
          <button onClick={() => setFilters(f => ({ ...f, q: '' }))} className="btn btn-ghost icon-only btn-sm">
            <IconX size={12} />
          </button>
        )}
      </div>

      <Select
        value={filters.status}
        onChange={v => setFilters(f => ({ ...f, status: v }))}
        options={[
          ['', 'All status'],
          ['2xx', `2xx · success${statusCounts['2xx'] ? ` (${statusCounts['2xx']})` : ''}`],
          ['4xx', `4xx · client error${statusCounts['4xx'] ? ` (${statusCounts['4xx']})` : ''}`],
          ['5xx', `5xx · server error${statusCounts['5xx'] ? ` (${statusCounts['5xx']})` : ''}`],
        ]}
        width={200}
      />
      {(filters.q || filters.status) && (
        <button className="btn btn-ghost btn-sm" onClick={() => setFilters({ q: '', status: '' })}>
          Clear
        </button>
      )}
      {count > 0 && onClearAll && (
        <button
          className="btn btn-ghost btn-sm"
          onClick={onClearAll}
          title={pinnedCount > 0
            ? `Delete all captured events except the ${pinnedCount} pinned one${pinnedCount === 1 ? '' : 's'}`
            : 'Delete all captured events on this endpoint'}
          style={{ color: '#dc2626', gap: 6 }}
        >
          <IconTrash size={13} />
          <span>Clear{pinnedCount > 0 ? ' unpinned' : ' all events'}</span>
        </button>
      )}
    </div>
  );
}

function Select({ value, onChange, options, width = 160 }) {
  return (
    <div style={{ position: 'relative', width }}>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        style={{
          width: '100%', appearance: 'none',
          padding: '7px 32px 7px 12px',
          background: 'var(--neutral-25)',
          border: '1px solid var(--border-default)',
          borderRadius: 8, fontSize: 13, color: 'var(--fg-1)',
          fontFamily: 'inherit', cursor: 'pointer',
        }}>
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
      <IconChevDown size={14} stroke="var(--fg-3)" style={{
        position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none',
      }} />
    </div>
  );
}

export function RequestList({ events, filtered, density, selectedId, setSelectedId, onDelete, onTogglePin, newIds, readOnly }) {
  return (
    <>
      <ListHeader />
      <div style={{ flex: 1, overflow: 'auto' }}>
        {filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--fg-3)', fontSize: 13 }}>
            No requests match your filters.
          </div>
        ) : filtered.map(evt => (
          <RequestRow
            key={evt.id}
            event={evt}
            selected={evt.id === selectedId}
            onClick={() => setSelectedId(evt.id)}
            onDelete={readOnly ? null : () => onDelete(evt.id)}
            onTogglePin={readOnly ? null : () => onTogglePin(evt.id)}
            density={density}
            isNew={newIds.has(evt.id)}
          />
        ))}
      </div>
    </>
  );
}

function ListHeader() {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '22px 1fr auto',
      alignItems: 'center', gap: 12,
      padding: '10px 14px',
      background: 'var(--neutral-25)',
      borderBottom: '1px solid var(--border-default)',
      fontSize: 11, fontWeight: 600, color: 'var(--fg-3)',
      textTransform: 'uppercase', letterSpacing: '0.06em',
      position: 'sticky', top: 0, zIndex: 2,
    }}>
      <span></span>
      <span>Request</span>
      <span style={{ textAlign: 'right' }}>Status</span>
    </div>
  );
}

function RequestRow({ event, selected, onClick, onDelete, onTogglePin, density, isNew }) {
  const [hover, setHover] = useState(false);
  const rowPad = density === 'compact' ? '8px 14px' : '12px 14px';
  const sId = shortIdOf(event.id);
  const ip = event.ip || event.headers?.['x-forwarded-for'] || event.headers?.['X-Forwarded-For'];
  const provider = providerOf(event.headers?.['x-yuno-provider'] || event.providerKey);
  const ok = event.responseCode >= 200 && event.responseCode < 300;
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className={isNew ? 'row-enter' : ''}
      style={{
        display: 'grid',
        gridTemplateColumns: '22px 1fr auto',
        alignItems: 'center', gap: 12,
        padding: rowPad,
        borderBottom: '1px solid var(--border-subtle)',
        background: selected ? 'var(--yuno-purple-50)' : '#fff',
        borderLeft: selected ? '2px solid var(--yuno-purple-600)' : '2px solid transparent',
        cursor: 'pointer', transition: 'background 120ms var(--ease-out)',
        position: 'relative',
      }}>
      <ProviderBadge provider={provider} size={22} />
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <Chip tone={ok ? 'brand' : 'danger'} size="sm" style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, fontSize: 10.5, padding: '1px 6px', flexShrink: 0 }}>{event.method}</Chip>
          <code style={{
            fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 600,
            color: selected ? 'var(--yuno-purple-700)' : 'var(--fg-1)',
            background: 'transparent', padding: 0, whiteSpace: 'nowrap',
          }}>#{sId}</code>
          {ip && (
            <code style={{
              fontFamily: 'var(--font-mono)', fontSize: 12,
              color: selected ? 'var(--yuno-purple-700)' : 'var(--fg-2)',
              background: 'transparent', padding: 0, whiteSpace: 'nowrap',
              overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0,
            }}>{ip}</code>
          )}
        </div>
        <div style={{ fontSize: 11.5, color: 'var(--fg-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: 4, fontFamily: 'var(--font-mono)', display: 'flex', alignItems: 'center', gap: 6 }}>
          <span>{formatLocalDateTime(event.receivedAt)}</span>
          {event.pinned && (
            <span title="Pinned — kept beyond retention" style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '1px 6px', borderRadius: 4, background: 'var(--yuno-purple-50)', color: '#4c1d95', fontFamily: 'var(--font-sans)', fontSize: 10.5, fontWeight: 600, letterSpacing: '0.02em', textTransform: 'uppercase' }}>
              <IconPin size={9} filled /> Pinned
            </span>
          )}
        </div>
      </div>
      {!ok && (
        <Chip tone="danger" size="sm" style={{ fontFamily: 'var(--font-mono)' }}>{event.responseCode}</Chip>
      )}
      {(event.pinned || hover) && onTogglePin && (
        <button
          onClick={(e) => { e.stopPropagation(); onTogglePin(); }}
          className="btn btn-ghost icon-only btn-sm"
          title={event.pinned ? 'Pinned — kept beyond retention. Click to unpin.' : 'Pin to keep beyond retention'}
          style={{
            position: 'absolute', right: hover && onDelete ? 44 : 12, top: '50%', transform: 'translateY(-50%)',
            background: event.pinned ? 'var(--yuno-purple-50)' : '#fff',
            border: '1px solid', borderColor: event.pinned ? '#ddd6fe' : 'var(--border-default)',
            color: event.pinned ? 'var(--yuno-purple-600)' : 'var(--fg-3)',
          }}
        ><IconPin size={12} filled={event.pinned} /></button>
      )}
      {hover && onDelete && (
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="btn btn-ghost icon-only btn-sm"
          title="Delete event"
          style={{
            position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
            background: '#fff', border: '1px solid var(--border-default)',
            color: '#dc2626',
          }}
        ><IconTrash size={12} /></button>
      )}
    </div>
  );
}

export function EmptyState({ baseUrl, endpointId }) {
  const url = `${baseUrl}/u/${endpointId}`;
  return (
    <div style={{
      flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      padding: 48, textAlign: 'center',
    }}>
      <div style={{ position: 'relative', width: 120, height: 120, marginBottom: 24 }}>
        {[0, 1, 2].map(i => (
          <div key={i} className="ping-ring" style={{ animationDelay: `${i * 0.8}s` }} />
        ))}
        <div style={{
          position: 'absolute', inset: '35%',
          background: 'var(--yuno-purple-600)', color: '#fff',
          borderRadius: 999, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <IconSignal size={20} stroke="#fff" />
        </div>
      </div>
      <h3 style={{ fontSize: 18, fontWeight: 600, color: 'var(--fg-1)', margin: 0 }}>Waiting for your first request…</h3>
      <p style={{ fontSize: 14, color: 'var(--fg-3)', maxWidth: 420, marginTop: 8 }}>
        Send any HTTP request to your endpoint URL and it will appear here instantly.
      </p>
      <code style={{
        marginTop: 14, fontFamily: 'var(--font-mono)', fontSize: 12.5,
        background: 'var(--neutral-25)', padding: '8px 12px',
        borderRadius: 6, border: '1px solid var(--border-subtle)',
        userSelect: 'all', wordBreak: 'break-all', maxWidth: 480,
      }}>{url}</code>
    </div>
  );
}
